import { z } from 'zod';
import { teamSchema, teamSlugSchema } from '../schemas/team.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, paginationQuerySchema, } from './_shared.js';
/**
 * Bounded set of `team_reprovision_results.outcome` values. Must stay in
 * lockstep with `teamReprovisionResultOutcomeSchema` in `endpoints/admin-teams.ts`
 * and the `lastReprovisionOutcomeSchema` private const in `schemas/team.ts`.
 */
const PROVISION_OUTCOMES = ['reprovisioned', 'noop', 'skipped', 'failed'];
/**
 * `GET /api/teams` — paginated team list, gate `teams:view`. Optional
 * `search` runs ILIKE across `name`, `display_name`, and `description`
 * server-side. `sort` is a signed comma-joined field list (validated
 * server-side against the WHITELIST). `provisionStatus` is a comma-joined
 * multi-filter over the four canonical outcome values.
 */
export const listTeamsQuerySchema = listQueryBase.extend({
    search: z.string().min(1).max(200).optional(),
    provisionStatus: multiFilter(PROVISION_OUTCOMES),
});
/**
 * `GET /api/teams/export` — same filters + sort as the list, without pagination.
 * Used to stream all matching rows as a `text/csv` attachment.
 */
export const exportTeamsQuerySchema = listTeamsQuerySchema.omit({
    page: true,
    pageSize: true,
});
export const listTeamsResponseSchema = paginatedResponse(teamSchema);
/**
 * `POST /api/teams` body — provisioning entry (§5.5). `adminUserIds` is the
 * initial team admin set recorded in the queue's per-job metadata so the
 * provisioning worker can grant the role after step 7 commits. 1-10 admins
 * per provision call.
 *
 * `name` reuses the shared `teamSlugSchema` from `schemas/team.ts` so the
 * create-side regex stays in lockstep with the entity row.
 */
export const createTeamBodySchema = z
    .object({
    name: teamSlugSchema,
    displayName: z.string().min(1).max(120),
    adminUserIds: z
        .string()
        .uuid()
        .array()
        .min(1)
        .max(10)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: 'adminUserIds must be unique',
    }),
})
    .strict();
/**
 * `POST /api/teams` returns 202 — provisioning is async via BullMQ. Response
 * carries the freshly inserted source id (the team row itself does not
 * exist yet — it's created in step 7 of the worker) so the UI can poll for
 * status. The team id is intentionally absent: it does not exist at request
 * time, and an always-null wire field carried no information.
 */
export const createTeamResponseSchema = z.object({
    sourceId: z.string().uuid(),
});
/** Re-export of the canonical id-param schema for naming clarity at call sites. */
export const teamIdParamSchema = idParamSchema;
/**
 * `GET /api/teams/:id` — single-resource fetch. Returns the canonical
 * `teamSchema` row so callers can re-load after a PATCH without re-listing.
 */
export const getTeamResponseSchema = teamSchema;
/**
 * `GET /api/teams/by-name/:name` path params. `name` reuses the shared
 * `teamSlugSchema` so the slug regex stays in lockstep with the entity row and
 * `createTeamBodySchema.name`.
 *
 * Exists because `/teams/:slug` addresses a team by name: without a
 * slug-addressed route the UI resolved the slug by scanning a paginated list
 * page, which drops every team outside the first page. See ADR 0009.
 */
export const getTeamByNameParamsSchema = z.object({ name: teamSlugSchema });
/** Same row shape as `GET /api/teams/:id` — one projection, two addresses. */
export const getTeamByNameResponseSchema = teamSchema;
/**
 * `PATCH /api/teams/:id` — display-name / description edits. At least one
 * field must be present (zero-field PATCH → 400).
 */
export const updateTeamBodySchema = z
    .object({
    displayName: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
})
    .strict()
    .refine((b) => b.displayName !== undefined || b.description !== undefined, {
    message: 'at least one of displayName or description must be provided',
});
export const updateTeamResponseSchema = teamSchema;
// `DELETE /api/teams/:id` reuses `teamIdParamSchema` directly — no
// dedicated `deleteTeamParamsSchema` is exported because the registry
// references the shared param schema. Returns 204 (no body).
/**
 * `GET /api/teams/:id/admins` query — page + pageSize only. The admin set is
 * bounded; the service deliberately exposes no `search` filter, so we strip
 * `search` from the shared pagination shape rather than advertise a param the
 * service ignores. (Grant rows embed `displayName`/`email` — see
 * `teamAdminGrantSchema` — so there is no client-side `/api/users` join.)
 */
export const listTeamAdminsQuerySchema = paginationQuerySchema.omit({ search: true });
/**
 * `GET /api/teams/:id/admins` — list active `team_admin` grants for a team.
 * Embeds the grantee's `displayName` + `email` (server-side `LEFT JOIN users`)
 * so the roster renders names without a client-side join against the capped
 * `/api/users` page — that join silently dropped admins whose account fell
 * outside the newest page once a deployment crossed `pageSize` users. Both are
 * nullable: an orphaned/soft-deleted grant (no matching user row) yields nulls
 * rather than failing response validation, and the UI falls back to a
 * truncated id. `teams:view` already gates this route, so embedding the name
 * does not widen the disclosure surface.
 */
export const teamAdminGrantSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    teamId: z.string().uuid(),
    createdAt: z.string().datetime(),
    adoSyncPending: z.boolean(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
export const listTeamAdminsResponseSchema = paginatedResponse(teamAdminGrantSchema);
/**
 * `POST /api/teams/:id/admins` — grant `team_admin` to a user for the team
 * identified by `:id`. Idempotent on existing non-deleted grants. Returns
 * the grant row (201 on fresh insert, 200 on idempotent re-grant).
 */
export const addTeamAdminBodySchema = z.object({ userId: z.string().uuid() }).strict();
export const addTeamAdminResponseSchema = teamAdminGrantSchema;
/**
 * `DELETE /api/teams/:id/admins/:userId` — soft-revoke a `team_admin` grant.
 * Service-layer last-admin guard prevents the team from becoming admin-less
 * (409 `LAST_ADMIN`).
 */
export const removeTeamAdminParamsSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
});
/**
 * `GET /api/teams/:id/reviewers` query — page + pageSize only (mirrors the
 * admins list; no `search` — the reviewer set is narrow and bounded).
 */
export const listTeamReviewersQuerySchema = paginationQuerySchema.omit({ search: true });
/**
 * `GET /api/teams/:id/reviewers` — active `team_reviewers` association rows.
 * Embeds the reviewer's `displayName` + `email` (server-side `LEFT JOIN users`),
 * mirroring `teamAdminGrantSchema` — see that schema for why both are nullable.
 */
export const teamReviewerGrantSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    teamId: z.string().uuid(),
    createdAt: z.string().datetime(),
    adoSyncPending: z.boolean(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
export const listTeamReviewersResponseSchema = paginatedResponse(teamReviewerGrantSchema);
/**
 * `POST /api/teams/:id/reviewers` — add a marketplace user as a reviewer.
 * Idempotent on existing non-deleted rows (201 fresh, 200 idempotent re-add).
 * Any marketplace user may be added (decision 11 — intentional).
 */
export const addTeamReviewerBodySchema = z.object({ userId: z.string().uuid() }).strict();
export const addTeamReviewerResponseSchema = teamReviewerGrantSchema;
/**
 * `DELETE /api/teams/:id/reviewers/:userId` — soft-remove a reviewer. No
 * last-reviewer guard: an empty Reviewers team is a valid (gating) state.
 */
export const removeTeamReviewerParamsSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
});
/**
 * `GET /api/teams/:id/members` query — page + pageSize only (mirrors the
 * reviewers list; no `search`). The member set is a bounded roster.
 */
export const listTeamMembersQuerySchema = paginationQuerySchema.omit({ search: true });
/**
 * `GET /api/teams/:id/members` — active member association rows
 * (`team_associations` with `kind='member'`). Mirrors `teamReviewerGrantSchema`
 * MINUS `adoSyncPending`: a member is a plain user↔team association with NO
 * permissions and NO ADO mirror, so there is nothing to report a pending sync
 * for. Embeds `displayName` + `email` (server `LEFT JOIN users`); both nullable
 * for an orphaned grant — see `teamAdminGrantSchema`.
 */
export const teamMemberGrantSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    teamId: z.string().uuid(),
    createdAt: z.string().datetime(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
export const listTeamMembersResponseSchema = paginatedResponse(teamMemberGrantSchema);
/**
 * `POST /api/teams/:id/members` — add a marketplace user as a plain team
 * member. Idempotent on existing non-deleted rows (201 fresh, 200 idempotent
 * re-add). No ADO sync, no org-membership gate — any marketplace user may be
 * added. Reuses the `teams:manage_admins` gate (no new permission).
 */
export const addTeamMemberBodySchema = z.object({ userId: z.string().uuid() }).strict();
export const addTeamMemberResponseSchema = teamMemberGrantSchema;
/**
 * `DELETE /api/teams/:id/members/:userId` — soft-remove a member association.
 * No last-member guard — a team with zero members is a valid state.
 */
export const removeTeamMemberParamsSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
});
//# sourceMappingURL=teams.js.map